import type {
  Field,
  KizenFieldType,
  ApiFieldToModifyOption,
} from 'pages/AutomationEngine/types';
import { type FieldToModifyAction } from './types';
import { type FieldsToModifyValues } from './fieldsToModifyContext';

import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';

import { getPayloadFieldValue } from 'services/FieldService';
import { FIELD_TYPES } from 'utility/constants';
import {
  defaultFieldValueMappingTypes,
  fieldValueMappingsForFlow,
  fieldValueMappingsErrorsForFlow,
  removeDeletedOptionsInFieldValueMappings,
} from '../CommonComponents/valueMappingsCommon';
import {
  EMPTY_CLEAR_FIELDS_KEY,
  cfvChangeTypeOptionsConst,
} from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/common';

import {
  getTeamLabel,
  relatedIdToOption,
  displayNameToOptionError,
  namedToOptionError,
  variableToOptionDeleted,
} from 'services/helpers';

import {
  _checkDeleted,
  displayNameDescriptor,
  descriptionDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';

import {
  hasOptionsWizard,
  usingRelatedObject,
  isValueTypeRelated,
  isValueTypeEntityVariableField,
} from './common';

import UUID from 'utility/UUID';

import {
  MessageDictionary,
  AddErrorMessage,
} from '__pages/AutomationEngine/messageDictionary';

import {
  mapToFlowFieldToClear,
  mapToApiFieldToClear,
} from 'pages/AutomationEngine/dialogs/fieldMapper';

type ValueListLike = Array<{ id: string }>;
type ExtendedField = Field & { deleted: boolean; displayName: string };

const isNaInt = (value: any) => isNaN(parseInt(value));

const parseIntOrNull = (value: any) =>
  isNaInt(value) ? null : parseInt(value);

export const fieldToModifyfieldType = (ftm: any) => ftm?.fieldData?.fieldType;

const justTheIds = (valueList: ValueListLike) => valueList.map(({ id }) => id);

const isValidArray = (list: Array<any>) => Array.isArray(list) && list.length;

const isEmptyArray = (list: Array<any>) =>
  Array.isArray(list) && list.length === 0;

const listOrId = (value: any) =>
  isValidArray(value) ? justTheIds(value) : value;

const validForOption = (value: any) => Boolean(value?.id);

const getValueForApi = (field: Field, fieldValue: any) => {
  const { value } = fieldValue;
  switch (field.fieldType) {
    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
      return value?.id ?? null;
    case FIELD_TYPES.Checkbox.type:
      return value || false;
    case FIELD_TYPES.Checkboxes.type:
    case FIELD_TYPES.Files.type:
      if (isValidArray(value)) {
        return justTheIds(value);
      }
      return null;
    case FIELD_TYPES.Relationship.type:
      if (value) {
        return isValidArray(value)
          ? justTheIds(value)
          : isEmptyArray(value)
            ? value
            : value.id;
      }
      return null;
    case FIELD_TYPES.TeamSelector.type:
      return value?.id || null;
    case FIELD_TYPES.Choices.type:
      return value ? String(value) : null;
    case FIELD_TYPES.Date.type:
      return getPayloadFieldValue(value, field) || null;
    case FIELD_TYPES.Text.type:
    case FIELD_TYPES.LongText.type:
      return value || '';
    case FIELD_TYPES.Decimal.type:
    case FIELD_TYPES.Money.type:
    case FIELD_TYPES.Integer.type:
      return isNaN(parseInt(value)) ? null : parseFloat(value);
    case FIELD_TYPES.DynamicTags.type: {
      return {
        tags_to_add: listOrId(fieldValue.tagsToAdd),
        tags_to_remove: listOrId(fieldValue.tagsToRemove),
      };
    }
    default: {
      return value || null;
    }
  }
};

const getRelatedObjectField = (
  relatedObjectField: ExtendedField,
  primaryRelatedObjectField: ExtendedField
) => {
  const field: ExtendedField | null =
    relatedObjectField || primaryRelatedObjectField;
  return {
    label: field?.displayName,
    value: field?.id,
    deleted: !!field?.deleted,
  };
};

const getThisRecordField = (thisRecordField: ExtendedField) => {
  const field: ExtendedField | null = thisRecordField;
  return {
    label: field?.displayName,
    value: field?.id,
    fieldData: field,
    deleted: !!field?.deleted,
  };
};

const getRelatedObject = (relatedObject: any) => {
  return {
    value: relatedObject?.id,
    label: relatedObject?.objectName,
    deleted: !!relatedObject?.deleted,
    relation: {
      ...relatedObject,
      relatedObject: relatedObject?.id,
    },
  };
};

const getFieldToModify = (field: ExtendedField | null, translate: any) => {
  return translate(field);
};

const getAutomationTargetRelationshipField = (
  automationTargetRelationshipField: any
) => {
  return {
    label: automationTargetRelationshipField?.displayName,
    value: automationTargetRelationshipField?.id,
    deleted: !!automationTargetRelationshipField?.deleted,
  };
};

const getfieldValueMapping = (value: any, fieldType: KizenFieldType) =>
  fieldType === FIELD_TYPES.Rating.type ? parseIntOrNull(value) : value;

const getfieldValueMappings = (
  fieldValueMappings: Array<any>,
  fieldType: KizenFieldType
) => {
  const mapped = fieldValueMappings.map(
    ({ order, sourceValues, targetValues }) => ({
      order,
      sourceValues: (sourceValues || []).map((sourceValue: any) =>
        getfieldValueMapping(sourceValue?.value || sourceValue, fieldType)
      ),
      targetValues: (targetValues || [])
        .filter(Boolean)
        .map((targetValue: any) =>
          getfieldValueMapping(targetValue?.value || targetValue, fieldType)
        ),
    })
  );
  return mapped;
};

const checkClearListDeleted = (
  messageDictionary: MessageDictionary,
  addErrorMessage: AddErrorMessage,
  parentObject: any,
  descriptorCallback: any,
  itemName: string = '',
  objectPath: string = 'undefined',
  objectErrorTrans: (o: any) => void = (o: any) => o,
  checkChildren: boolean = true
) => {
  const errors = {};

  if (itemName && !parentObject?.length) {
    addErrorMessage(
      `${messageDictionary.thereWasanErrorLoadingThe} '${itemName}'`
    );
    _set(errors, objectPath, [EMPTY_CLEAR_FIELDS_KEY]);
    return errors;
  }
  if (checkChildren) {
    const errorList = (parentObject || []).reduce((acc: any, object: any) => {
      const error = _checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: object,
        descriptorCallback,
        objectPath: 'name',
        objectErrorTrans,
      });
      if (!_isEmpty(error)) {
        acc.push(error);
      }
      return acc;
    }, []);
    if (errorList.length) {
      _set(
        errors,
        objectPath,
        errorList.flatMap(({ name }: any) => name)
      );
    }
  }

  return errors;
};

const forApi = (step: { details: FieldsToModifyValues }) => {
  const actions = step.details.actions.map((action) => {
    const {
      fieldToModify,
      specificFieldValue,
      changeType,
      relatedObject,
      automationTargetRelationshipField,
      relatedObjectField,
      fieldResolution,
      fieldValueMappings,
      thisRecordField,
      variable,
      updateMode,
      fieldsToClear,
    } = action;

    const changeTypeValue =
      typeof changeType === 'string' ? changeType : changeType.value;

    return {
      fieldToModify: fieldToModify?.id,
      specificFieldValue:
        changeTypeValue === cfvChangeTypeOptionsConst.SPECIFIC_VALUE &&
        fieldToModify
          ? getValueForApi(fieldToModify, specificFieldValue)
          : null,
      changeType: changeTypeValue,
      relatedObject:
        changeTypeValue && isValueTypeRelated(changeTypeValue)
          ? relatedObject?.value
          : null,
      automationTargetRelationshipField:
        automationTargetRelationshipField?.value,
      relatedObjectField:
        changeTypeValue === cfvChangeTypeOptionsConst.THIS_RECORD_FIELD
          ? thisRecordField?.value
          : relatedObjectField?.value,
      fieldResolution: fieldResolution?.value ?? 'overwrite',
      fieldValueMappings: fieldValueMappings?.length
        ? getfieldValueMappings(
            fieldValueMappings,
            fieldToModifyfieldType(fieldToModify)
          )
        : null,
      variable: variable?.value,
      updateMode,
      fieldsToClear: Array.isArray(fieldsToClear)
        ? fieldsToClear.map(mapToApiFieldToClear)
        : undefined,
    };
  });

  return { details: { actions } };
};

const valueWrap = (value: any) => ({ value });

const getValueForFlow = (
  field: Field | null,
  specificFieldValue: any,
  changeTypeValue: any
) => {
  const { value } = specificFieldValue || { value: null };

  switch (field?.fieldType) {
    case FIELD_TYPES.YesNo.type:
    case FIELD_TYPES.YesNoMaybe.type:
      return valueWrap(specificFieldValue?.id);

    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
    case FIELD_TYPES.Checkboxes.type:
      return valueWrap(specificFieldValue);
    case FIELD_TYPES.Checkbox.type:
      return valueWrap(value || false);

    case FIELD_TYPES.Files.type:
      return valueWrap(specificFieldValue);
    case FIELD_TYPES.Relationship.type:
      // todo ====>> look for deleted field info in the paylaod
      if (specificFieldValue) {
        if (Array.isArray(specificFieldValue)) {
          return valueWrap(specificFieldValue.map(relatedIdToOption));
        }
        if (validForOption(specificFieldValue)) {
          return valueWrap(relatedIdToOption(specificFieldValue));
        }
        // KZN-5591 if we got a singleton with no id in it throw error, as it means the relation has been deleted
        throw new Error();
      }
      return valueWrap(null);
    case FIELD_TYPES.TeamSelector.type:
      if (specificFieldValue) {
        return valueWrap({
          ...specificFieldValue,
          displayName: getTeamLabel(specificFieldValue),
        });
      }
      return valueWrap(null);
    case FIELD_TYPES.Choices.type:
      return valueWrap(specificFieldValue ? String(specificFieldValue) : null);
    case FIELD_TYPES.Date.type:
      return valueWrap(getPayloadFieldValue(value, field) || null);
    case FIELD_TYPES.DateTime.type:
      return valueWrap(value);
    case FIELD_TYPES.Text.type:
    case FIELD_TYPES.LongText.type:
    case FIELD_TYPES.Email.type:
      return valueWrap(value || '');
    case FIELD_TYPES.PhoneNumber.type:
      // TODO: remove it once we migrate our DB and update our API do not send object anymore.
      return valueWrap(
        value?.formatted ??
          value?.formattedNationalNumber ??
          value?.formatted_national_number ??
          value?.e164 ??
          value
      );
    case FIELD_TYPES.Decimal.type:
    case FIELD_TYPES.Money.type:
    case FIELD_TYPES.Integer.type:
      return valueWrap(isNaN(parseInt(value)) ? null : parseFloat(value));

    case FIELD_TYPES.DynamicTags.type: {
      if (
        specificFieldValue &&
        !isValueTypeEntityVariableField(changeTypeValue)
      ) {
        const tagsMapDown = (tag: any) => ({
          ...tag,
          value: tag.id,
          label: tag.name,
        });
        const { tagsToAdd, tagsToRemove } = specificFieldValue;
        return {
          ...specificFieldValue,
          tagsToAdd: tagsToAdd.map(tagsMapDown),
          tagsToRemove: tagsToRemove.map(tagsMapDown),
          value: specificFieldValue.tagsToAdd,
        };
      }
      return valueWrap(null);
    }

    case FIELD_TYPES.Timezone.type:
      return valueWrap(value || null);

    case FIELD_TYPES.Rating.type:
      return valueWrap(isNaN(parseInt(value)) ? null : parseInt(value));

    default: {
      return valueWrap(specificFieldValue || null);
    }
  }
};

type ApiFieldToModifyAction = Omit<FieldToModifyAction, 'fieldsToClear'> & {
  fieldsToClear: Array<ApiFieldToModifyOption>;
};

type ApiFieldsToModifyValues = Omit<FieldsToModifyValues, 'actions'> & {
  actions: Array<ApiFieldToModifyAction>;
};

const forFlow = (
  apiObject: ApiFieldsToModifyValues,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  let allFieldErrors = {};
  const actions = (apiObject.actions || []).map((action) => {
    const {
      fieldToModify,
      specificFieldValue,
      changeType: changeTypeValue,
      primaryRelatedObjectField,
      automationTargetRelationshipField,
      relatedObjectField,
      relatedObject,
      fieldResolution,
      fieldValueMappings: _fieldValueMappings,
      variable,
      updateMode,
      fieldsToClear,
    } = action;

    let fieldValueMappings = fieldValueMappingsForFlow(_fieldValueMappings);
    let transformedValue;
    let initialValidation = {};

    const errors = {
      ..._checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: fieldToModify,
        descriptorCallback: displayNameDescriptor,
        objectPath: 'fieldToModify',
        objectErrorTrans: displayNameToOptionError,
      }),
      ..._checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: relatedObject,
        descriptorCallback: descriptionDescriptor,
        objectPath: 'relatedObject',
        objectErrorTrans: namedToOptionError,
      }),
      ..._checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: automationTargetRelationshipField,
        descriptorCallback: displayNameDescriptor,
        objectPath: 'automationTargetRelationshipField',
        objectErrorTrans: displayNameToOptionError,
      }),
      ..._checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: relatedObjectField,
        descriptorCallback: displayNameDescriptor,
        objectPath: 'relatedObjectField',
        objectErrorTrans: displayNameToOptionError,
      }),
      ..._checkDeleted({
        messageDictionary,
        addErrorMessage,
        objectToCheck: variable,
        descriptorCallback: descriptionDescriptor,
        objectPath: 'variable',
        objectErrorTrans: namedToOptionError,
      }),
      ...(updateMode === 'clear_fields'
        ? checkClearListDeleted(
            messageDictionary,
            addErrorMessage,
            fieldsToClear,
            displayNameDescriptor,
            messageDictionary.fieldsToClear,
            'fieldsToClear',
            ({ id }) => id
          )
        : undefined),
      // find and spread the first error in the specificFieldValue
      ...[
        ...((typeof specificFieldValue !== 'string' &&
          specificFieldValue?.tagsToAdd) ||
          []),
        ...((typeof specificFieldValue !== 'string' &&
          specificFieldValue?.tagsToRemove) ||
          []),
        ...(Array.isArray(specificFieldValue)
          ? specificFieldValue
          : [specificFieldValue]),
      ].reduce((acc, value) => {
        return Object.keys(acc).length
          ? acc
          : _checkDeleted({
              messageDictionary,
              addErrorMessage,
              objectToCheck: value,
              descriptorCallback: nameDescriptor,
              objectPath: 'specificFieldValue',
            });
      }, {}),
    };

    const withOptionWizard = hasOptionsWizard(
      fieldToModify,
      changeTypeValue,
      relatedObjectField
    );

    if (withOptionWizard) {
      ({ fieldValueMappings, initialValidation } =
        removeDeletedOptionsInFieldValueMappings({
          fieldToModify,
          sourceOptions: relatedObjectField?.options,
          targetOptions: fieldToModify?.options,
          addErrorMessage,
          messageDictionary,
          fieldValueMappings,
        }));
    }

    try {
      transformedValue =
        (changeTypeValue as unknown) ===
        cfvChangeTypeOptionsConst.SPECIFIC_VALUE
          ? getValueForFlow(fieldToModify, specificFieldValue, changeTypeValue)
          : null;
    } catch (error) {
      addErrorMessage(
        `${
          messageDictionary.thereWasanErrorLoadingThe
        } '${fieldToModify?.displayName || messageDictionary.associatedItem}'.`
      );

      transformedValue = null;
    }
    const ftm = getFieldToModify(
      fieldToModify as ExtendedField,
      (field: Field) => ({
        ...field,
        access: { edit: true, remove: true },
      })
    );

    const key = UUID.generate();

    if (Object.keys(errors).length) {
      allFieldErrors = { ...allFieldErrors, [key]: errors };
    }

    const automationTargetRelationshipFieldValue =
      getAutomationTargetRelationshipField(automationTargetRelationshipField);

    const flow = {
      ...action,
      key,
      fieldToModify: ftm,
      specificFieldValue: transformedValue,
      changeType: { value: changeTypeValue },
      relatedObject: getRelatedObject(relatedObject),
      automationTargetRelationshipField: automationTargetRelationshipFieldValue,
      relatedObjectField:
        !automationTargetRelationshipFieldValue.deleted &&
        usingRelatedObject(changeTypeValue)
          ? getRelatedObjectField(
              relatedObjectField as ExtendedField,
              primaryRelatedObjectField as ExtendedField
            )
          : null,
      thisRecordField:
        (changeTypeValue as unknown) ===
        cfvChangeTypeOptionsConst.THIS_RECORD_FIELD
          ? getThisRecordField(relatedObjectField as ExtendedField)
          : null,

      fieldResolution: { value: fieldResolution || 'overwrite' },
      fieldValueMappings,
      fieldValueMappingsTypes: defaultFieldValueMappingTypes,
      fieldValueMappingsErrors:
        fieldValueMappingsErrorsForFlow(fieldValueMappings),
      fieldValueMappingsValidation:
        fieldValueMappingsErrorsForFlow(fieldValueMappings),
      variable: variable ? variableToOptionDeleted(variable) : null,
      errors,
      updateMode,
      fieldsToClear: Array.isArray(fieldsToClear)
        ? fieldsToClear.map(mapToFlowFieldToClear)
        : undefined,
      ...initialValidation,
    };

    return flow;
  });

  return { actions, allFieldErrors, isChangeActionMutable: false };
};

export const changeFieldValueDTO = {
  forApi,
  forFlow,
};
